import Vue from 'vue'
import App from './app.vue'
import Rollbar from 'rollbar'
import VueGtm from '@gtm-support/vue2-gtm';
import {
  BootstrapVue,
  IconsPlugin
} from 'bootstrap-vue'
import vueNumeralFilterInstaller from 'vue-numeral-filter'
import VueDOMPurifyHTML from 'vue-dompurify-html'
import VueCurrencyInput from 'vue-currency-input'
import VueMask, { VueMaskDirective } from 'v-mask'
import { API_HOST } from '@src/app.config'
import axios from 'axios'
import router from '@router'
import store from '@state/store'
import { getStringFromObjectAddress } from '@utils/format-address'
import '@components/_globals'
import globalMixin from './mixins/GlobalMixin.vue';
import './assets/tailwind.css'

// Install BootstrapVue
Vue.use(BootstrapVue)
// Optionally install the BootstrapVue icon components plugin
Vue.use(IconsPlugin)

Vue.use(VueMask)
Vue.directive('mask', VueMaskDirective)

Vue.use(vueNumeralFilterInstaller, { locale: 'en-gb' })

Vue.use(require('vue-moment'))
Vue.use(VueDOMPurifyHTML)
Vue.use(VueCurrencyInput, {
  globalOptions: {
    locale : 'en',  // enforce the default formatting to English standards
  }
})

Vue.filter('getStringFromObjectAddress', getStringFromObjectAddress)

// Don't warn about using the dev version of Vue in development.
Vue.config.productionTip = process.env.NODE_ENV === 'production'

// load gtm
const gtmKey = process.env.VUE_APP_GTM_ID
if (gtmKey){
  Vue.use(VueGtm, {
    id: gtmKey, // Your GTM single container ID, array of container ids ['GTM-xxxxxx', 'GTM-yyyyyy'] or array of objects [{id: 'GTM-xxxxxx', queryParams: { gtm_auth: 'abc123', gtm_preview: 'env-4', gtm_cookies_win: 'x'}}, {id: 'GTM-yyyyyy', queryParams: {gtm_auth: 'abc234', gtm_preview: 'env-5', gtm_cookies_win: 'x'}}], // Your GTM single container ID or array of container ids ['GTM-xxxxxx', 'GTM-yyyyyy']
    queryParams: {
      // Add URL query string when loading gtm.js with GTM ID (required when using custom environments)
    },
    defer: false, // Script can be set to `defer` to speed up page load at the cost of less accurate results (in case visitor leaves before script is loaded, which is unlikely but possible). Defaults to false, so the script is loaded `async` by default
    compatibility: false, // Will add `async` and `defer` to the script tag to not block requests for old browsers that do not support `async`
    // nonce: '2726c7f26c', // Will add `nonce` to the script tag
    enabled: true, // defaults to true. Plugin can be disabled by setting this to false for Ex: enabled: !!GDPR_Cookie (optional)
    debug: process.env.NODE_ENV !== 'production', // Whether or not display console logs debugs (optional)
    loadScript: true, // Whether or not to load the GTM Script (Helpful if you are including GTM manually, but need the dataLayer functionality in your components) (optional)
    // ignoredViews: ['homepage'], // Don't trigger events for specified router names (optional)
    trackOnNextTick: false, // Whether or not call trackView in Vue.nextTick
    vueRouter: router, // Pass the router instance to automatically sync with router (optional)
  });
}

if (process.env.VUE_APP_ROLLBAR) {
  // Set the Rollbar instance in the Vue prototype
  // before creating the first Vue instance.
  // This ensures it is available in the same way for every
  // instance in your app.
  Vue.prototype.$rollbar = new Rollbar({
    accessToken: process.env.VUE_APP_ROLLBAR,
    captureUncaught: true,
    captureUnhandledRejections: true,
  });
  let rollbarEnv = 'local'
  if (process.env.VUE_APP_ROLLBAR_ENV) {
    rollbarEnv = process.env.VUE_APP_ROLLBAR_ENV
  } else {
    console.log('Rollbar is missing Environment, defaulting to local')
  }
  Vue.prototype.$rollbar.configure({
    reportLevel: 'error',
    logLevel: 'info',
    payload: { environment: rollbarEnv }
  });
} else {
  console.log('Rollbar not configured on this system')
}

// If running inside Cypress...
if (process.env.VUE_APP_TEST === 'e2e') {
  // Ensure tests fail when Vue emits an error.
  Vue.config.errorHandler = window.Cypress.cy.onUncaughtException
} else if (process.env.VUE_APP_ROLLBAR) {
  // If you have already set up a global error handler,
  // just add `vm.$rollbar.error(err)` to the top of it.
  // If not, this simple example will preserve the app's existing
  // behavior while also reporting uncaught errors to Rollbar.
  Vue.config.errorHandler = (err, vm, info) => {
    vm.$rollbar.error(err);
    throw err; // rethrow
  };
}

axios.defaults.baseURL = API_HOST

Vue.mixin(globalMixin);

const app = new Vue({
  router,
  store,
  render: (h) => h(App),
}).$mount('#app')

axios.interceptors.response.use(function (response) {
  // Any status code that lie within the range of 2xx cause this function to trigger
  // Do something with response data
  return response;
}, function (error) {
  // Any status codes that falls outside the range of 2xx cause this function to trigger
  // Do something with response error

  if (!app.$store){
    // early return as Vue doesn't exist yet
    return Promise.reject(error);
  }

  // only care about this logic for internal APIs
  if (error
      && error.response
      && error.response.config
      && error.response.config.baseURL
      && error.response.config.baseURL === API_HOST){

    // Deal with Re-Auth

    const code = error.response.status;
    const path = error.response.config.url.slice(error.response.config.baseURL.length);

    const validationPaths = ['/oauth/token','/me']
    if (app.$route.name !== 'login'){
      if (code === 401 && validationPaths.indexOf(path) === -1){
        // trigger `/me` revalidation
        app.$store.dispatch('auth/validate')
      }else if (path === '/oauth/token'){
        // deauth user via state methods
        app.$store.dispatch('auth/deAuthUser')
      }
    }

    // Custom Handler for known 500's
    if (app.$route.name !== 'login'){
      if (code === 500){
        const innerCode = error.response.data.error.code
        const message = error.response.data.error.message

        // Deal Elastic Shenanigans
        if (innerCode === 429 && message.startsWith('429 Too Many Request')){
          const isTmrActive = Boolean(window.localStorage.getItem('lilberd-tmr-active'))
          if (!isTmrActive){
            window.localStorage.setItem('lilberd-tmr-active', JSON.stringify(true))
            window.localStorage.setItem('lilberd-tmr-start', JSON.stringify(Date.now()))
            console.log('SERVER IS CURRENTLY BUSY, PLEASE TRY AGAIN LATER')
            app.$store.dispatch('errors/setFourZeroNine', true)
          }
        }
      }
    }
  }
  return Promise.reject(error);
});

// If running e2e tests...
if (process.env.VUE_APP_TEST === 'e2e') {
  // Attach the app to the window, which can be useful
  // for manually setting state in Cypress commands
  // such as `cy.logIn()`.
  window.__app__ = app
}
setTimeout(() => {
  console.log(
    '%cWarning!',
    'color: orange; background: #202124; font-size: 24px; font-weight: bold;'
  );
  console.log(
    '%cNever run any code / script here that you yourself don\'t understand \nBy running code here you are acknowledging that any outcome, adverse or benign; including but not limited to `corruption of data`, resulting from any script you run, is at your own culpability.',
    'color: orange; background: #202124; font-size: 16px;'
  );
}, 2000);
